import React from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { BrainCircuit } from "lucide-react";
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsModule = ({
  // Translation & Layout
  t,
  lang,
  session,

  // General settings state
  me, setMe,
  xApiKey, setXApiKey, hasXKey, setHasXKey,
  camerasText, setCamerasText, savedCameras, setSavedCameras,
  positives,
  positivesInput, setPositivesInput,
  plateFilter,
  threshold,
  gap,

  // VIP plates state
  vipPlates,
  vipInput, setVipInput,
  vipDescription, setVipDescription,
  syncingVips, setSyncingVips,

  // Kassen settings
  kassenUID, setKassenUID,
  kassenCameraID, setKassenCameraID,
  savedKassenUID, setSavedKassenUID,
  savedKassenCameraID, setSavedKassenCameraID,

  // Shipping settings
  shippingUID, setShippingUID,
  shippingCameraID, setShippingCameraID,
  savedShippingUID, setSavedShippingUID,
  savedShippingCameraID, setSavedShippingCameraID,

  // Shelly settings
  shellyServer, setShellyServer,
  shellyAuthKey, setShellyAuthKey,
  shellyDevices, setShellyDevices, // [{id: "", name: ""}]
  savedShellyServer, setSavedShellyServer,
  savedShellyAuthKey, setSavedShellyAuthKey,
  savedShellyDevices, setSavedShellyDevices, // [{id: "...", name: "..."}]

  // Live View settings
  liveViews, setLiveViews,         // [{name: "", embed: ""}]
  savedLiveViews, setSavedLiveViews, // persisted array from server

  // Twilio
  twilioAccountSid, setTwilioAccountSid,
  twilioAuthToken, setTwilioAuthToken,
  twilioPhoneNumber, setTwilioPhoneNumber,
  savedTwilioAccountSid, setSavedTwilioAccountSid,
  savedTwilioAuthToken, setSavedTwilioAuthToken,
  savedTwilioPhoneNumber, setSavedTwilioPhoneNumber,

  // AI Analytics settings
  aiAnalyticsCameraId, setAiAnalyticsCameraId,
  aiAnalyticsGeminiKey, setAiAnalyticsGeminiKey,
  aiAnalyticsGeminiKeySaved, setAiAnalyticsGeminiKeySaved,
  aiAnalyticsHelixUid, setAiAnalyticsHelixUid,
  aiAnalyticsOrgId, setAiAnalyticsOrgId,

  // Webhook state
  webhookConfig,
  webhookURL, setWebhookURL,
  webhookNotifications,
  loadingWebhook,
  newWebhookToken, setNewWebhookToken,
  creatingWebhook, setCreatingWebhook,
  savingPrefs, setSavingPrefs,

  // Functions from App.js
  refreshPositives,
  refreshVipPlates,
  loadWebhookConfig,
  loadWebhookEvents,
  formatTimestampWithTimezone,
  setActiveTab,
  setAnalysisSubTab,
}) => {

  // --- Settings-specific functions ---

  const saveSettings = async () => {
    try {
      setSavingPrefs(true);
      const camArr = (camerasText || "").split(/[\n,; ]+/).map(s => s.trim()).filter(Boolean);
      const prefs = {
        camera_ids: camArr,
        plate_filter: plateFilter,
        default_threshold: threshold,
        default_gap: gap,
        region: me.region,
        timezone: me.timezone,
      };
      if (xApiKey) prefs.x_api_key = xApiKey;
      if (aiAnalyticsOrgId) prefs.ai_analytics_org_id = aiAnalyticsOrgId;
      
      if (kassenUID.trim()) prefs.kassen_uid = kassenUID.trim();
      if (kassenCameraID.trim()) prefs.kassen_camera_id = kassenCameraID.trim();
      if (shippingUID.trim()) prefs.shipping_uid = shippingUID.trim();
      if (shippingCameraID.trim()) prefs.shipping_camera_id = shippingCameraID.trim();
      
      if (shellyServer.trim()) prefs.shelly_server = shellyServer.trim();
      if (shellyAuthKey.trim()) prefs.shelly_auth_key = shellyAuthKey.trim();
      // Shelly devices: always send (allows add, edit AND delete)
      const validDevices = shellyDevices
        .filter(d => (d.id || '').trim())
        .map(d => ({ id: d.id.trim(), name: (d.name || '').trim() }));
      prefs.shelly_devices = validDevices;
      // Live Views: always send (allows add, edit AND delete). Drop rows where
      // either Name or Embed is empty — matches the confirmed plan.
      const validLiveViews = (liveViews || [])
        .map(lv => ({ name: (lv.name || '').trim(), embed: (lv.embed || '').trim() }))
        .filter(lv => lv.name && lv.embed);
      prefs.live_views = validLiveViews;
      // Twilio
      if (twilioAccountSid.trim()) prefs.twilio_account_sid = twilioAccountSid.trim();
      if (twilioAuthToken.trim()) prefs.twilio_auth_token = twilioAuthToken.trim();
      if (twilioPhoneNumber.trim()) prefs.twilio_phone_number = twilioPhoneNumber.trim();
      
      const res = await axios.patch(`${API}/user/settings`, { preferences: prefs }, { headers: session.authHeaders() });
      if (res.data?.updated) {
        try {
          const meUser = await axios.get(`${API}/user/me`, { headers: session.authHeaders() });
          const p = meUser.data.preferences || {};
          if (p.camera_ids && p.camera_ids.length) { setCamerasText(p.camera_ids.join(",")); setSavedCameras(p.camera_ids); }
          if (p.default_threshold != null) { /* threshold is read-only here */ }
          if (p.default_gap != null) { /* gap is read-only here */ }
          if (p.region || p.timezone) setMe(m => ({...m, region: p.region || m.region, timezone: p.timezone || m.timezone }));
          setHasXKey(!!p.x_api_key);
          if (p.ai_analytics_org_id) setAiAnalyticsOrgId(p.ai_analytics_org_id);
          
          if (p.kassen_uid) setSavedKassenUID(p.kassen_uid);
          if (p.kassen_camera_id) setSavedKassenCameraID(p.kassen_camera_id);
          if (p.shipping_uid) setSavedShippingUID(p.shipping_uid);
          if (p.shipping_camera_id) setSavedShippingCameraID(p.shipping_camera_id);
          
          if (p.shelly_server) setSavedShellyServer(p.shelly_server);
          if (p.shelly_auth_key) setSavedShellyAuthKey(true);
          // Reload devices array (with legacy fallback) and sync edit-array
          if (p.shelly_devices && Array.isArray(p.shelly_devices)) {
            setSavedShellyDevices(p.shelly_devices);
            setShellyDevices(p.shelly_devices.map(d => ({ id: d.id || '', name: d.name || '' })));
          } else {
            const legacy = [];
            if (p.shelly_device_id_1) legacy.push({ id: p.shelly_device_id_1, name: p.shelly_device_name_1 || '' });
            if (p.shelly_device_id_2) legacy.push({ id: p.shelly_device_id_2, name: p.shelly_device_name_2 || '' });
            setSavedShellyDevices(legacy);
            setShellyDevices(legacy.map(d => ({ id: d.id, name: d.name })));
          }
          // Reload live_views (always an array — save endpoint sets it)
          if (Array.isArray(p.live_views)) {
            const clean = p.live_views.map(lv => ({
              name: (lv && lv.name) || '',
              embed: (lv && lv.embed) || '',
            }));
            setSavedLiveViews(clean);
            setLiveViews(clean.map(lv => ({ name: lv.name, embed: lv.embed })));
          } else {
            setSavedLiveViews([]);
            setLiveViews([]);
          }
          if (p.twilio_account_sid) setSavedTwilioAccountSid(true);
          if (p.twilio_auth_token) setSavedTwilioAuthToken(true);
          if (p.twilio_phone_number) setSavedTwilioPhoneNumber(p.twilio_phone_number);
        } catch (re) { }
        setXApiKey("");
        setKassenUID("");
        setKassenCameraID("");
        setShippingUID("");
        setShippingCameraID("");
        setShellyServer("");
        setShellyAuthKey("");
        setTwilioAccountSid("");
        setTwilioAuthToken("");
        setTwilioPhoneNumber("");
        alert(t('settings_saved_alert'));
      } else {
        alert(t('settings_no_changes_alert'));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Speichern fehlgeschlagen";
      alert(msg);
    } finally {
      setSavingPrefs(false);
    }
  };

  const addPositives = async () => {
    try {
      const items = (positivesInput || "").split(/[\n,; ]+/).map(s => s.trim()).filter(Boolean);
      if (!items.length) return alert("Bitte Kennzeichen eingeben");
      const res = await axios.post(`${API}/user/positives`, { plates: items }, { headers: session.authHeaders() });
      if (res.data?.added >= 0) {
        setPositivesInput("");
        await refreshPositives();
        alert(res.data.added + " Kennzeichen hinzugefügt.");
      } else {
        alert("Keine Kennzeichen hinzugefügt.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Hinzufügen fehlgeschlagen";
      alert(msg);
    }
  };

  const deletePositives = async () => {
    if (!window.confirm(t('confirm_delete_plates'))) return;
    try {
      const items = (positivesInput || "").split(/[\n,; ]+/).map(s => s.trim()).filter(Boolean);
      if (!items.length) return alert("Bitte Kennzeichen eingeben");
      await axios.delete(`${API}/user/positives`, { data: { plates: items }, headers: session.authHeaders() });
      setPositivesInput("");
      await refreshPositives();
      alert("Positive Kennzeichen gelöscht.");
    } catch (e) {
      alert("Löschen fehlgeschlagen");
    }
  };

  const addVipPlate = async () => {
    if (!vipInput.trim()) {
      alert("Bitte Kennzeichen eingeben");
      return;
    }
    try {
      const res = await axios.post(`${API}/user/vips`, {
        license_plate: vipInput.trim(),
        description: vipDescription.trim()
      }, { headers: session.authHeaders() });
      
      if (res.data?.added > 0) {
        alert(res.data.message || "VIP Kennzeichen hinzugefügt");
        setVipInput("");
        setVipDescription("");
        await refreshVipPlates();
      } else {
        alert(res.data?.message || "Kennzeichen bereits vorhanden");
      }
    } catch (e) {
      alert("Hinzufügen fehlgeschlagen");
    }
  };

  const addVipPlateToVerkada = async () => {
    if (!vipInput.trim()) {
      alert("Bitte Kennzeichen eingeben");
      return;
    }
    try {
      const res = await axios.post(`${API}/user/vips/verkada`, {
        license_plate: vipInput.trim(),
        description: vipDescription.trim()
      }, { headers: session.authHeaders() });
      
      if (res.data?.created > 0) {
        alert(res.data.message || "VIP Kennzeichen in Verkada erstellt");
        setVipInput("");
        setVipDescription("");
        await refreshVipPlates();
      } else {
        alert(res.data?.message || "Kennzeichen bereits vorhanden");
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Verkada Erstellung fehlgeschlagen";
      alert(`Verkada Fehler: ${errorMsg}`);
    }
  };

  const deleteVipPlate = async (licensePlate) => {
    if (!window.confirm(t('settings_vip_delete_confirm').replace('{plate}', licensePlate))) return;
    
    try {
      const res = await axios.delete(`${API}/user/vips`, {
        data: { license_plate: licensePlate },
        headers: session.authHeaders()
      });
      
      if (res.data?.removed > 0) {
        alert("VIP Kennzeichen gelöscht");
        await refreshVipPlates();
      } else {
        alert("Kennzeichen nicht gefunden");
      }
    } catch (e) {
      alert("Löschen fehlgeschlagen");
    }
  };

  const syncVipPlates = async () => {
    setSyncingVips(true);
    try {
      const res = await axios.post(`${API}/user/vips/sync`, {}, { headers: session.authHeaders() });
      
      if (res.data?.synced) {
        const { verkada_vips, manual_vips, total_vips, message } = res.data;
        alert(`Verkada Sync erfolgreich!\n\n${message}\n\nDetails:\n• ${verkada_vips} Verkada LPOIs\n• ${manual_vips} manuelle VIPs\n• ${total_vips} VIPs insgesamt`);
        await refreshVipPlates();
      } else {
        alert("Sync fehlgeschlagen");
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Sync fehlgeschlagen";
      alert(`Verkada Sync Fehler:\n\n${errorMsg}`);
    } finally {
      setSyncingVips(false);
    }
  };

  const updateWebhookConfig = async (config) => {
    if (!session.userToken) return;
    try {
      await axios.patch(`${API}/user/webhook-config`, config, {
        headers: { 'X-User-Session-Token': session.userToken }
      });
      await loadWebhookConfig();
    } catch (error) {
    }
  };

  const createWebhookConfigHandler = async () => {
    if (!session.userToken || !newWebhookToken.trim()) return;
    if (newWebhookToken.trim().length < 6) {
      alert('Webhook-Token muss mindestens 6 Zeichen haben');
      return;
    }
    setCreatingWebhook(true);
    try {
      const response = await axios.post(`${API}/user/webhook-config`, {
        webhook_token: newWebhookToken.trim(),
        enabled: true
      }, {
        headers: { 'X-User-Session-Token': session.userToken }
      });
      if (response.data.created) {
        const baseUrl = window.location.origin;
        const createdToken = response.data.webhook_token || newWebhookToken.trim();
        setWebhookURL(`${baseUrl}/api/webhook/${createdToken}`);
        setNewWebhookToken("");
        await loadWebhookConfig();
        alert('Webhook-Konfiguration erfolgreich erstellt!');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Erstellen der Webhook-Konfiguration';
      alert(msg);
    } finally {
      setCreatingWebhook(false);
    }
  };

  const copyWebhookURL = async () => {
    try {
      await navigator.clipboard.writeText(webhookURL);
      alert(t('webhook_url_copied'));
    } catch (error) {
    }
  };

  return (
    <Card className="panel lightCard" style={{marginTop: 8}}>
      <CardContent>
        <div className="panel-title darktxt">{t('settings_title')}</div>
        <div className="field">
          <Label title="Verkada API Key">{t('x_api_key_new')}</Label>
          {hasXKey && <div className="small">{t('x_api_key_saved_note')}</div>}
          <Textarea value={xApiKey} onChange={(e) => setXApiKey(e.target.value)} placeholder={hasXKey ? "(gespeichert) – zum Ändern neuen Key einfügen" : "Einmalig einfügen, dann bleibt er gespeichert"} className={hasXKey && !xApiKey ? 'mutedInput' : ''} />
        </div>
        <div className="field">
          <Label>{t('org_id')}</Label>
          {!!aiAnalyticsOrgId && <div className="small" style={{color: '#059669'}}>{t('org_id_saved')} {aiAnalyticsOrgId}</div>}
          <Input 
            value={aiAnalyticsOrgId}
            onChange={(e) => setAiAnalyticsOrgId(e.target.value)} 
            placeholder={t('org_id_placeholder')}
          />
        </div>
        <div className="field twoCol">
          <div>
            <Label>{t('region')}</Label>
            <Input value={me.region || ''} onChange={(e)=> setMe(m => ({...m, region: e.target.value}))} placeholder="eu" />
          </div>
          <div>
            <Label>{t('timezone')}</Label>
            <Input value={me.timezone || ''} onChange={(e)=> setMe(m => ({...m, timezone: e.target.value}))} placeholder="Europe/Berlin" />
          </div>
        </div>
        <div className="field">
          <Label title="Ersetzt die gespeicherte Liste">{t('camera_replace')}</Label>
          {!!savedCameras.length && <div className="small">Gespeichert: {savedCameras.join(", ")}</div>}
          <Textarea value={camerasText} onChange={(e) => setCamerasText(e.target.value)} placeholder={savedCameras.length ? "(gespeichert) – zum Ändern hier neue Liste einfügen" : "Mehrere IDs durch Komma oder Zeilen trennen"} className={savedCameras.length && !camerasText ? 'mutedInput' : ''} />
        </div>
        <div className="panel-title darktxt" style={{marginTop:12}}>{t('positives_title')}</div>
        <div className="field">
          <Textarea value={positivesInput} onChange={(e) => setPositivesInput(e.target.value)} placeholder="Mehrere Kennzeichen, getrennt durch Zeilen oder Komma" />
          <div className="actions">
            <Button onClick={addPositives} disabled={!positivesInput.trim()}>{t('positives_add')}</Button>
            <Button variant="secondary" onClick={deletePositives} disabled={!positivesInput.trim()}>{t('positives_delete')}</Button>
          </div>
          <div className="small" style={{marginTop:8}}>Aktuell positiv: {positives.length ? positives.join(", ") : "–"}</div>
        </div>

        {/* VIP Plates Section */}
        <div className="panel-title darktxt" style={{marginTop:12}}>{t('settings_vip_plates_title')}</div>
        <div className="field">
          <Label>{t('settings_vip_plates_add')}</Label>
          <div className="field twoCol">
            <div>
              <Input 
                value={vipInput} 
                onChange={(e) => setVipInput(e.target.value)} 
                placeholder={t('plate_input_placeholder')} 
              />
            </div>
            <div>
              <Input 
                value={vipDescription} 
                onChange={(e) => setVipDescription(e.target.value)} 
                placeholder="Beschreibung (optional)" 
              />
            </div>
          </div>
          <div className="actions">
            <Button onClick={addVipPlate} disabled={!vipInput.trim()}>
              Lokal hinzufügen
            </Button>
            <Button onClick={addVipPlateToVerkada} disabled={!vipInput.trim()} variant="default">
              🔗 Verkada erstellen
            </Button>
            <Button variant="secondary" onClick={syncVipPlates} disabled={syncingVips}>
              {syncingVips ? 'Synchronisiere...' : '🔄 Verkada Sync'}
            </Button>
          </div>
          <div className="small" style={{marginTop: 8, color: '#6b7280'}}>
            <strong>Lokal:</strong> {t('settings_vip_plates_local_only')}<br/>
            <strong>Verkada:</strong> Direkt in Verkada LPOI erstellt und synchronisiert<br/>
            <strong>Sync:</strong> Alle Verkada LPOI abrufen und lokal aktualisieren
          </div>
        </div>
        
        {/* VIP List */}
        {vipPlates.length > 0 && (
          <div className="field">
            <Label>Aktuelle VIP Kennzeichen ({vipPlates.length})</Label>
            <div className="vipList" style={{marginTop: 8}}>
              {vipPlates.map((vip, idx) => {
                const isVerkada = vip.source === 'verkada';
                return (
                  <div key={idx} className="vipItem" style={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '8px 12px',
                    marginBottom: '4px',
                    backgroundColor: isVerkada ? 'rgba(59,130,246,0.1)' : 'rgba(239,68,68,0.1)',
                    borderRadius: '4px',
                    border: `1px solid ${isVerkada ? 'rgba(59,130,246,0.2)' : 'rgba(239,68,68,0.2)'}`
                  }}>
                    <div>
                      <strong style={{color: isVerkada ? '#2563eb' : '#dc2626'}}>
                        {isVerkada ? '🔗' : '📍'} {vip.license_plate}
                      </strong>
                      {vip.description && <span className="small" style={{marginLeft: 8, color: '#6b7280'}}>– {vip.description}</span>}
                      <span className="small" style={{marginLeft: 8, color: '#9ca3af'}}>
                        ({isVerkada ? 'Verkada LPOI' : 'Lokal'})
                      </span>
                    </div>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => deleteVipPlate(vip.license_plate)}
                      style={{fontSize: '12px', padding: '4px 8px'}}
                    >
                      {isVerkada ? 'Verkada löschen' : 'Löschen'}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="small" style={{marginTop: 8, color: '#6b7280'}}>
              🔗 = Verkada LPOI (wird auch in Verkada gelöscht)<br/>
              📍 = Lokal (nur in dieser Anwendung)
            </div>
          </div>
        )}

        {/* Kassenmodul Einstellungen */}
        <div className="panel-title darktxt" style={{marginTop: 20, paddingTop: 16, borderTop: '2px solid #e5e7eb'}}>💰 Kassenmodul</div>
        
        {!savedKassenUID && !savedKassenCameraID && (
          <div style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '16px'
          }}>
            <div style={{fontWeight: 'bold', color: '#92400e', marginBottom: '4px'}}>{t('settings_kasse_not_configured')}</div>
            <div style={{fontSize: '14px', color: '#92400e'}}>
              Für die Verkada Video Tagging Integration benötigen Sie:<br/>
              • Eine gültige UID (Verkada Helix Endpoint)<br/>
              • Eine Kamera-ID für den Kassenbereich<br/>
              • Einen API-Schlüssel mit Video Tagging Berechtigung
            </div>
          </div>
        )}

        <div className="field twoCol">
          <div>
            <Label>UID (Verkada Helix)</Label>
            {!!savedKassenUID && <div className="small">Gespeichert: {savedKassenUID}</div>}
            <Input 
              value={kassenUID} 
              onChange={(e) => setKassenUID(e.target.value)} 
              placeholder={savedKassenUID ? "(gespeichert) – zum Ändern neue UID eingeben" : "Verkada Helix Endpoint UID eingeben"} 
              className={savedKassenUID && !kassenUID ? 'mutedInput' : ''}
            />
          </div>
          <div>
            <Label>{t('settings_kasse_camera_id')}</Label>
            {!!savedKassenCameraID && <div className="small">Gespeichert: {savedKassenCameraID}</div>}
            <Input 
              value={kassenCameraID} 
              onChange={(e) => setKassenCameraID(e.target.value)} 
              placeholder={savedKassenCameraID ? "(gespeichert) – zum Ändern neue ID eingeben" : "Verkada Kamera-ID für Kassenbereich"} 
              className={savedKassenCameraID && !kassenCameraID ? 'mutedInput' : ''}
            />
          </div>
        </div>
        <div className="small" style={{marginTop: 8, color: '#6b7280'}}>
          <strong>UID:</strong> {t('settings_kasse_endpoint_desc')}<br/>
          <strong>{t('settings_camera_id_label')}</strong> Verkada Kamera-ID zur Überwachung des Kassenbereichs
        </div>

        {/* AI Analytics Configuration */}
        <div className="panel-title darktxt" style={{marginTop: 20, paddingTop: 16, borderTop: '2px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '8px'}}>
          <BrainCircuit size={20} color="#6366f1" /> {t('ai_analytics_settings_title')}
        </div>
        
        <div className="field">
          <Label>{t('ai_analytics_camera_id')}</Label>
          {!!aiAnalyticsCameraId && <div className="small" style={{color: '#059669'}}>{t('ai_analytics_camera_id_saved')} {aiAnalyticsCameraId}</div>}
          <Input 
            value={aiAnalyticsCameraId}
            onChange={(e) => setAiAnalyticsCameraId(e.target.value)} 
            placeholder={t('ai_analytics_camera_id_placeholder')}
          />
        </div>
        
        <div className="field">
          <Label>{t('ai_analytics_gemini_key')}</Label>
          {aiAnalyticsGeminiKeySaved ? (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#ecfdf5',
              border: '1px solid #10b981',
              borderRadius: '6px',
              color: '#059669',
              fontWeight: '500',
              fontSize: '14px',
              marginBottom: '8px'
            }}>
              {t('ai_analytics_gemini_key_saved')}
            </div>
          ) : null}
          <Input 
            type="password"
            value={aiAnalyticsGeminiKey}
            onChange={(e) => setAiAnalyticsGeminiKey(e.target.value)} 
            placeholder={aiAnalyticsGeminiKeySaved ? "••••••••••••••••" : t('ai_analytics_gemini_key_placeholder')}
          />
          <div className="small" style={{marginTop: 4, color: '#6b7280'}}>
            {lang === 'de' ? 'Der API-Key wird sicher gespeichert und nicht mehr angezeigt.' : 'The API key is stored securely and will not be displayed again.'}
          </div>
        </div>
        
        <div className="field">
          <Label>{t('ai_analytics_helix_uid')}</Label>
          {!!aiAnalyticsHelixUid && <div className="small" style={{color: '#059669'}}>{t('ai_analytics_helix_uid_saved')} {aiAnalyticsHelixUid}</div>}
          <Input 
            value={aiAnalyticsHelixUid}
            onChange={(e) => setAiAnalyticsHelixUid(e.target.value)} 
            placeholder={t('ai_analytics_helix_uid_placeholder')}
          />
        </div>
        
        <div className="actions" style={{marginTop: 12}}>
          <Button onClick={async () => {
            try {
              const prefs = {};
              if (aiAnalyticsCameraId) prefs.ai_analytics_camera_id = aiAnalyticsCameraId;
              if (aiAnalyticsGeminiKey) prefs.ai_analytics_gemini_key = aiAnalyticsGeminiKey;
              if (aiAnalyticsHelixUid) prefs.ai_analytics_helix_uid = aiAnalyticsHelixUid;
              
              await axios.patch(`${API}/user/settings`, { preferences: prefs }, { headers: session.authHeaders() });
              
              if (aiAnalyticsGeminiKey) {
                setAiAnalyticsGeminiKeySaved(true);
                setAiAnalyticsGeminiKey("");
              }
              alert(t('ai_analytics_saved_success'));
            } catch (e) {
              alert('Error saving AI Analytics settings');
            }
          }}>
            {t('ai_analytics_save')}
          </Button>
        </div>

        {/* Shipping System Configuration */}
        <div className="panel-title darktxt" style={{marginTop: 20, paddingTop: 16, borderTop: '2px solid #e5e7eb'}}>📦 {t('shipping_title')}</div>
        <div className="small" style={{marginBottom: 16, color: '#6b7280'}}>
          {t('shipping_subtitle')}
        </div>
        {!savedShippingUID && !savedShippingCameraID && (
          <div className="small" style={{
            background: '#f59e0b10', 
            border: '1px solid #f59e0b40', 
            borderRadius: '6px', 
            padding: '12px', 
            marginBottom: '16px',
            color: '#92400e'
          }}>
            ⚠️ Versandmodul nicht konfiguriert
          </div>
        )}
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
          <div>
            <Label>{t('shipping_uid_label')}</Label>
            {!!savedShippingUID && <div className="small">{t('settings_saved')}: {savedShippingUID}</div>}
            <Input 
              value={shippingUID} 
              onChange={(e) => setShippingUID(e.target.value)} 
              placeholder={savedShippingUID ? t('shipping_uid_placeholder_saved') : t('shipping_uid_placeholder')} 
              className={savedShippingUID && !shippingUID ? 'mutedInput' : ''}
            />
          </div>
          <div>
            <Label>{t('shipping_camera_label')}</Label>
            {!!savedShippingCameraID && <div className="small">{t('settings_saved')}: {savedShippingCameraID}</div>}
            <Input 
              value={shippingCameraID} 
              onChange={(e) => setShippingCameraID(e.target.value)} 
              placeholder={savedShippingCameraID ? t('shipping_camera_placeholder_saved') : t('shipping_camera_placeholder')} 
              className={savedShippingCameraID && !shippingCameraID ? 'mutedInput' : ''}
            />
          </div>
        </div>
        <div className="small" style={{marginTop: 8, color: '#6b7280'}}>
          {t('shipping_settings_help')}
        </div>

        {/* Shelly Integration */}
        <div className="panel-title darktxt" style={{marginTop: 20, paddingTop: 16, borderTop: '2px solid #e5e7eb'}}>🏠 Shelly</div>
        <div className="small" style={{marginBottom: 16, color: '#6b7280'}}>
          Shelly Smart Home Geräte konfigurieren
        </div>
        
        <div className="field">
          <Label>{t('settings_shelly_server_label')}</Label>
          {!!savedShellyServer && <div className="small" style={{color: '#059669'}}>Gespeichert: {savedShellyServer}</div>}
          <Input 
            value={shellyServer} 
            onChange={(e) => setShellyServer(e.target.value)} 
            placeholder={savedShellyServer ? "(gespeichert) – zum Ändern neue URL eingeben" : "shelly-25-eu.shelly.cloud"} 
            className={savedShellyServer && !shellyServer ? 'mutedInput' : ''}
            data-testid="shelly-server-input"
          />
          <div className="small" style={{marginTop: 4, color: '#6b7280'}}>
            {t('settings_shelly_server_hint')}
          </div>
        </div>
        
        <div className="field">
          <Label>{t('settings_shelly_authkey_label')}</Label>
          {savedShellyAuthKey && <div className="small" style={{color: '#059669'}}>{t('settings_shelly_authkey_saved')}</div>}
          <Input 
            type="password"
            value={shellyAuthKey} 
            onChange={(e) => setShellyAuthKey(e.target.value)} 
            placeholder={savedShellyAuthKey ? "••••••••••••••••" : "Shelly Auth Key eingeben"} 
            className={savedShellyAuthKey && !shellyAuthKey ? 'mutedInput' : ''}
            data-testid="shelly-authkey-input"
          />
        </div>

        {/* Unified devices list: saved + editable + deletable */}
        <div className="field" style={{marginTop: '12px'}}>
          <Label>{t('settings_shelly_devices_title')} ({shellyDevices.length})</Label>
          {shellyDevices.length === 0 && (
            <div className="small" style={{color: '#6b7280', padding: '6px 0'}}>
              {t('settings_shelly_no_devices')}
            </div>
          )}
          {shellyDevices.map((dev, idx) => {
            const savedMatch = savedShellyDevices.find(s => s.id === dev.id);
            return (
              <div key={idx} style={{display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '8px', marginTop: '8px', alignItems: 'center'}}>
                <Input
                  value={dev.id}
                  onChange={(e) => {
                    const updated = [...shellyDevices];
                    updated[idx] = {...updated[idx], id: e.target.value};
                    setShellyDevices(updated);
                  }}
                  placeholder={t('settings_shelly_device_id_placeholder')}
                  data-testid={`shelly-device-id-${idx}`}
                />
                <Input
                  value={dev.name}
                  onChange={(e) => {
                    const updated = [...shellyDevices];
                    updated[idx] = {...updated[idx], name: e.target.value};
                    setShellyDevices(updated);
                  }}
                  placeholder={t('settings_shelly_device_name_placeholder')}
                  data-testid={`shelly-device-name-${idx}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  title={savedMatch ? t('settings_shelly_remove_saved') : t('settings_shelly_remove_entry')}
                  style={{padding: '8px', color: '#ef4444', minWidth: '36px'}}
                  onClick={() => {
                    const updated = shellyDevices.filter((_, i) => i !== idx);
                    setShellyDevices(updated);
                  }}
                  data-testid={`shelly-device-remove-${idx}`}
                >
                  ✕
                </Button>
              </div>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            style={{marginTop: '8px'}}
            onClick={() => setShellyDevices([...shellyDevices, {id: '', name: ''}])}
            data-testid="shelly-add-device"
          >
            {t('settings_shelly_add_device')}
          </Button>
          <div className="small" style={{marginTop: '8px', color: '#6b7280'}}>
            {t('settings_shelly_hint')}
          </div>
        </div>

        {/* Twilio Voice */}
        <div className="panel-title darktxt" style={{marginTop: 20, paddingTop: 16, borderTop: '2px solid #e5e7eb'}}>📞 {t('settings_twilio_title')}</div>
        <div className="small" style={{marginBottom: 16, color: '#6b7280'}}>
          {t('settings_twilio_subtitle')}
        </div>
        <div className="field">
          <Label>TWILIO_ACCOUNT_SID</Label>
          {savedTwilioAccountSid && <div className="small" style={{color: '#059669'}}>✓ {t('settings_saved_short')}</div>}
          <Input
            value={twilioAccountSid}
            onChange={(e) => setTwilioAccountSid(e.target.value)}
            placeholder={savedTwilioAccountSid ? "AC••••••••••••••••••••••••••••••" : "AC..."}
            data-testid="twilio-account-sid-input"
          />
        </div>
        <div className="field">
          <Label>TWILIO_AUTH_TOKEN</Label>
          {savedTwilioAuthToken && <div className="small" style={{color: '#059669'}}>✓ {t('settings_saved_short')}</div>}
          <Input
            type="password"
            value={twilioAuthToken}
            onChange={(e) => setTwilioAuthToken(e.target.value)}
            placeholder={savedTwilioAuthToken ? "••••••••••••••••" : ""}
            data-testid="twilio-auth-token-input"
          />
        </div>
        <div className="field">
          <Label>{t('settings_twilio_from_number')}</Label>
          {!!savedTwilioPhoneNumber && <div className="small" style={{color: '#059669'}}>Gespeichert: {savedTwilioPhoneNumber}</div>}
          <Input
            value={twilioPhoneNumber}
            onChange={(e) => setTwilioPhoneNumber(e.target.value)}
            placeholder="+4111111111111111"
            data-testid="twilio-phone-input"
          />
          <div className="small" style={{marginTop: 4, color: '#6b7280'}}>
            {t('settings_twilio_from_hint')}
          </div>
        </div>

        {/* Webhook Management */}
        <div className="panel-title darktxt" style={{marginTop: 20, paddingTop: 16, borderTop: '2px solid #e5e7eb'}}>🔗 {t('webhook_title')}</div>
        <div className="small" style={{marginBottom: 16, color: '#6b7280'}}>
          {t('webhook_subtitle')}
        </div>

        {loadingWebhook ? (
          <div style={{textAlign: 'center', padding: 20}}>{t('loading_webhook_config')}</div>
        ) : webhookConfig && webhookConfig.has_config ? (
          <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
            
            {/* Webhook URL */}
            <div className="field">
              <Label>{t('webhook_url_label')}</Label>
              <div className="small" style={{marginBottom: '8px', color: '#6b7280'}}>
                Diese URL wird von Verkada aufgerufen, wenn Events eintreten
              </div>
              <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                <Input 
                  value={webhookURL} 
                  onChange={(e) => setWebhookURL(e.target.value)}
                  style={{flex: 1, fontFamily: 'monospace', fontSize: '12px'}}
                  placeholder="https://ihre-domain.com/api/webhook/token"
                />
                <Button onClick={copyWebhookURL} variant="outline" size="sm">
                  {t('webhook_url_copy')}
                </Button>
                <Button 
                  onClick={async () => {
                    const urlParts = webhookURL.split('/api/webhook/');
                    if (urlParts.length < 2 || !urlParts[1].trim()) {
                      alert('Ungültige Webhook-URL. Format: .../api/webhook/IHR_TOKEN');
                      return;
                    }
                    const newToken = urlParts[1].trim();
                    if (newToken.length < 6) {
                      alert('Token muss mindestens 6 Zeichen haben');
                      return;
                    }
                    try {
                      await axios.post(`${API}/user/webhook-config`, {
                        webhook_token: newToken,
                        enabled: webhookConfig?.enabled ?? true
                      }, {
                        headers: { 'X-User-Session-Token': session.userToken }
                      });
                      await loadWebhookConfig();
                      alert('Webhook-Token erfolgreich gespeichert!');
                    } catch (error) {
                      const msg = error.response?.data?.detail || 'Fehler beim Speichern';
                      alert(msg);
                    }
                  }} 
                  variant="default" 
                  size="sm"
                >
                  💾 Speichern
                </Button>
              </div>
            </div>

            {/* Webhook Status */}
            <div className="field" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <input
                type="checkbox"
                checked={webhookConfig.enabled}
                onChange={(e) => updateWebhookConfig({enabled: e.target.checked})}
              />
              <Label>
                {webhookConfig.enabled ? t('webhook_enabled') : t('webhook_disabled')}
              </Label>
            </div>

            {/* Webhook Statistics */}
            <div className="field">
              <div className="panel-title darktxt" style={{fontSize: '14px', marginBottom: 8}}>
                {t('webhook_stats_title')}
              </div>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px'}}>
                <div style={{textAlign: 'center', padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px'}}>
                  <div style={{fontSize: '18px', fontWeight: 'bold', color: '#1f2937'}}>{webhookConfig.stats.total_events}</div>
                  <div style={{fontSize: '12px', color: '#6b7280'}}>{t('webhook_stats_total')}</div>
                </div>
                <div style={{textAlign: 'center', padding: '8px', backgroundColor: '#f0f9ff', borderRadius: '6px'}}>
                  <div style={{fontSize: '18px', fontWeight: 'bold', color: '#0369a1'}}>{webhookConfig.stats.events_today}</div>
                  <div style={{fontSize: '12px', color: '#6b7280'}}>{t('webhook_stats_today')}</div>
                </div>
                <div style={{textAlign: 'center', padding: '8px', backgroundColor: '#f0fdf4', borderRadius: '6px'}}>
                  <div style={{fontSize: '12px', fontWeight: 'bold', color: '#166534'}}>
                    {webhookConfig.stats.last_event ? new Date(webhookConfig.stats.last_event).toLocaleString('de-DE') : 'Nie'}
                  </div>
                  <div style={{fontSize: '12px', color: '#6b7280'}}>{t('webhook_stats_last')}</div>
                </div>
              </div>
            </div>

            {/* Auto Actions */}
            <div className="field">
              <div className="panel-title darktxt" style={{fontSize: '14px', marginBottom: 8}}>
                {t('webhook_auto_actions')}
              </div>
              <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="checkbox"
                    checked={webhookConfig.auto_actions?.auto_add_vip || false}
                    onChange={(e) => updateWebhookConfig({
                      auto_actions: {...(webhookConfig.auto_actions || {}), auto_add_vip: e.target.checked}
                    })}
                  />
                  <Label style={{fontSize: '13px'}}>
                    🚗 Auto-VIP bei Security Events & Person of Interest
                  </Label>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="checkbox"
                    checked={webhookConfig.auto_actions?.log_security_events || false}
                    onChange={(e) => updateWebhookConfig({
                      auto_actions: {...(webhookConfig.auto_actions || {}), log_security_events: e.target.checked}
                    })}
                  />
                  <Label style={{fontSize: '13px'}}>
                    🔐 Security Events loggen (Alarm & Zugang)
                  </Label>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="checkbox"
                    checked={webhookConfig.auto_actions?.alert_high_priority || false}
                    onChange={(e) => updateWebhookConfig({
                      auto_actions: {...(webhookConfig.auto_actions || {}), alert_high_priority: e.target.checked}
                    })}
                  />
                  <Label style={{fontSize: '13px'}}>
                    🚨 High-Priority Events markieren
                  </Label>
                </div>
              </div>
            </div>

            {/* Live Notifications */}
            {webhookNotifications.length > 0 && (
              <div className="field">
                <div className="panel-title darktxt" style={{fontSize: '14px', marginBottom: 8}}>
                  {t('webhook_notifications_title')}
                </div>
                <div style={{maxHeight: '150px', overflowY: 'auto'}}>
                  {webhookNotifications.map((notification, index) => (
                    <div key={notification.id || index} style={{
                      padding: '8px',
                      marginBottom: '4px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '4px',
                      borderLeft: '3px solid #f59e0b',
                      fontSize: '12px'
                    }}>
                      <div style={{fontWeight: 'bold'}}>🔔 {notification.type}</div>
                      <div style={{color: '#6b7280'}}>
                        {formatTimestampWithTimezone(notification.timestamp)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Webhook Events Button */}
            <Button 
              onClick={() => {
                loadWebhookEvents();
                setActiveTab('analyse');
                setAnalysisSubTab('webhook-events');
              }}
              variant="secondary"
              style={{alignSelf: 'flex-start'}}
            >
              📋 {t('webhook_events_title')}
            </Button>
          </div>
        ) : (
          <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
            <div style={{
              padding: '16px',
              backgroundColor: '#f0f9ff',
              borderRadius: '6px',
              border: '1px solid #0ea5e9'
            }}>
              <div style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '8px'}}>
                🔗 Webhook-Token konfigurieren
              </div>
              <div style={{fontSize: '12px', color: '#6b7280', marginBottom: '12px'}}>
                Erstellen Sie einen eindeutigen Token für Ihre Webhook-URL. Dieser Token identifiziert alle eingehenden Webhooks als zu Ihrem Account gehörig.
              </div>
              
              <div className="field">
                <Label>Webhook-Token (mindestens 6 Zeichen):</Label>
                <div style={{display: 'flex', gap: '8px'}}>
                  <Input
                    value={newWebhookToken}
                    onChange={(e) => setNewWebhookToken(e.target.value)}
                    placeholder={t('webhook_token_placeholder')}
                    style={{flex: 1}}
                  />
                  <Button 
                    onClick={createWebhookConfigHandler}
                    disabled={creatingWebhook || newWebhookToken.trim().length < 6}
                  >
                    {creatingWebhook ? 'Erstellt...' : 'Erstellen'}
                  </Button>
                </div>
              </div>
              
              <div style={{fontSize: '11px', color: '#6b7280', marginTop: '8px'}}>
                💡 <strong>Beispiel-URL:</strong> {window.location.origin}/api/webhook/<em>ihr-token</em><br/>
                📋 <strong>Verwendung:</strong> Diese URL in Verkada unter "Integrations" → "Webhooks" eintragen
              </div>
            </div>
          </div>
        )}

        {/* Live View */}
        <div className="panel-title darktxt" style={{marginTop: 20, paddingTop: 16, borderTop: '2px solid #e5e7eb'}}>📺 {t('settings_liveview_title')}</div>
        <div className="small" style={{marginBottom: 16, color: '#6b7280'}}>
          {t('settings_liveview_subtitle')}
        </div>

        <div className="field" data-testid="liveview-list">
          {(!liveViews || liveViews.length === 0) && (
            <div className="small" style={{color: '#6b7280', padding: '6px 0'}}>
              {t('settings_liveview_no_entries')}
            </div>
          )}
          {(liveViews || []).map((lv, idx) => {
            const savedMatch = (savedLiveViews || []).find(
              s => (s.name || '') === (lv.name || '') && (s.embed || '') === (lv.embed || '')
            );
            const mutedClass = savedMatch ? 'mutedInput' : '';
            return (
              <div
                key={idx}
                data-testid={`liveview-row-${idx}`}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '12px',
                  marginTop: '8px',
                  background: savedMatch ? '#f8fafc' : '#ffffff',
                }}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                  <div style={{fontWeight: 600, color: '#111827'}}>
                    {t('settings_liveview_entry_prefix')} {idx + 1}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={savedMatch ? t('settings_liveview_remove_saved') : t('settings_liveview_remove_entry')}
                    style={{padding: '4px 8px', color: '#ef4444', minWidth: '32px'}}
                    onClick={() => {
                      const updated = liveViews.filter((_, i) => i !== idx);
                      setLiveViews(updated);
                    }}
                    data-testid={`liveview-remove-${idx}`}
                  >
                    ✕
                  </Button>
                </div>
                <div style={{marginBottom: '8px'}}>
                  <Label>{t('settings_liveview_name_label')}</Label>
                  <Input
                    value={lv.name}
                    onChange={(e) => {
                      const updated = [...liveViews];
                      updated[idx] = {...updated[idx], name: e.target.value};
                      setLiveViews(updated);
                    }}
                    placeholder={t('settings_liveview_name_placeholder')}
                    className={mutedClass}
                    data-testid={`liveview-name-${idx}`}
                  />
                </div>
                <div>
                  <Label>{t('settings_liveview_embed_label')}</Label>
                  <Textarea
                    value={lv.embed}
                    onChange={(e) => {
                      const updated = [...liveViews];
                      updated[idx] = {...updated[idx], embed: e.target.value};
                      setLiveViews(updated);
                    }}
                    placeholder={t('settings_liveview_embed_placeholder')}
                    rows={3}
                    className={mutedClass}
                    style={{fontFamily: 'monospace', fontSize: '12px'}}
                    data-testid={`liveview-embed-${idx}`}
                  />
                </div>
              </div>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            style={{marginTop: '12px'}}
            onClick={() => setLiveViews([...(liveViews || []), {name: '', embed: ''}])}
            data-testid="liveview-add"
          >
            {t('settings_liveview_add')}
          </Button>
          <div className="small" style={{marginTop: '8px', color: '#6b7280'}}>
            {t('settings_liveview_hint')}
          </div>
        </div>

        <div className="actions" style={{marginTop: 12}}>
          <Button onClick={saveSettings} disabled={savingPrefs}>{savingPrefs ? 'Speichern…' : t('settings_save')}</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SettingsModule;
